import axios from '../../utils/axios';

const mapCountry = (country) => ({
  id: country.id,
  name: country.name,
  countryCode: country.country_alpha3,
  nbrRestaurants: country.nbr_restaurants,
});

const mapRestaurantRow = (restaurant, projects) => ({
  ...restaurant,
  aliasName: restaurant.alias_name,
  groupName: restaurant.group_name,
  cashSysName: restaurant.cash_sys_name,
  countryCode: restaurant.country_code,
  isActive: restaurant.is_active,
  projectId: restaurant.project_id,
  projectName: projects.find((project) => project.id === restaurant.project_id)?.name,
});

/**
 * Fetches all restaurants.
 * @returns {Promise<Array>}
 */
export const getRestaurants = async () => {
  const response = await axios.get('/restaurants');
  return response.data;
};

/**
 * Fetches a restaurant by ID.
 * @param {number|string} restaurantId - The restaurant ID.
 * @returns {Promise<Object|null>}
 */
export const getRestaurant = async (restaurantId) => {
  if (!restaurantId) return null;

  const response = await axios.get(`/restaurants/${restaurantId}`);
  return response.data;
};

/**
 * Fetches TrayHealth settings for a restaurant.
 * @param {number|string} restaurantId - The restaurant ID.
 * @returns {Promise<Object>}
 */
export const getRestaurantTrayHealthSettings = async (restaurantId) => {
  const response = await axios.get(`/restaurants/${restaurantId}/trayhealth-settings`);
  return response.data;
};

/**
 * Creates TrayHealth settings for a restaurant.
 * @param {Object} params
 * @param {number|string} params.restaurantId - The restaurant ID.
 * @param {Object} params.trayhealthSettings - TrayHealth settings payload in API format.
 * @returns {Promise<Object>}
 */
export const createRestaurantTrayHealthSettings = async ({ restaurantId, trayhealthSettings }) => {
  const response = await axios.post(`/restaurants/${restaurantId}/trayhealth-settings`, trayhealthSettings, {
    headers: {
      accept: 'application/json',
    },
  });
  return response.data;
};

/**
 * Updates TrayHealth settings for a restaurant.
 * @param {Object} params
 * @param {number|string} params.restaurantId - The restaurant ID.
 * @param {Object} params.trayhealthSettings - TrayHealth settings payload in API format.
 * @returns {Promise<Object>}
 */
export const updateRestaurantTrayHealthSettings = async ({ restaurantId, trayhealthSettings }) => {
  const response = await axios.put(`/restaurants/${restaurantId}/trayhealth-settings`, trayhealthSettings, {
    headers: {
      accept: 'application/json',
    },
  });
  return response.data;
};

/**
 * Deletes TrayHealth settings for a restaurant.
 * @param {number|string} restaurantId - The restaurant ID.
 * @returns {Promise<Object>}
 */
export const deleteRestaurantTrayHealthSettings = async (restaurantId) => {
  const response = await axios.delete(`/restaurants/${restaurantId}/trayhealth-settings`, {
    headers: {
      accept: 'application/json',
    },
  });
  return response.data;
};

/**
 * Fetches TrayScan settings for a restaurant.
 * @param {number|string} restaurantId - The restaurant ID.
 * @returns {Promise<Object>}
 */
export const getRestaurantTrayScanSettings = async (restaurantId) => {
  const response = await axios.get(`/restaurants/${restaurantId}/trayscan-settings`);
  return response.data;
};

/**
 * Creates TrayScan settings for a restaurant.
 * @param {Object} params
 * @param {number|string} params.restaurantId - The restaurant ID.
 * @param {Object} params.trayscanSettings - TrayScan settings payload in API format.
 * @returns {Promise<Object>}
 */
export const createRestaurantTrayScanSettings = async ({ restaurantId, trayscanSettings }) => {
  const response = await axios.post(`/restaurants/${restaurantId}/trayscan-settings`, trayscanSettings, {
    headers: {
      accept: 'application/json',
    },
  });
  return response.data;
};

/**
 * Updates TrayScan settings for a restaurant.
 * @param {Object} params
 * @param {number|string} params.restaurantId - The restaurant ID.
 * @param {Object} params.trayscanSettings - TrayScan settings payload in API format.
 * @returns {Promise<Object>}
 */
export const updateRestaurantTrayScanSettings = async ({ restaurantId, trayscanSettings }) => {
  const response = await axios.put(`/restaurants/${restaurantId}/trayscan-settings`, trayscanSettings, {
    headers: {
      accept: 'application/json',
    },
  });
  return response.data;
};

/**
 * Deletes TrayScan settings for a restaurant.
 * @param {number|string} restaurantId - The restaurant ID.
 * @returns {Promise<Object>}
 */
export const deleteRestaurantTrayScanSettings = async (restaurantId) => {
  const response = await axios.delete(`/restaurants/${restaurantId}/trayscan-settings`, {
    headers: {
      accept: 'application/json',
    },
  });
  return response.data;
};

/**
 * Fetches all projects.
 * @returns {Promise<Array>}
 */
export const getProjects = async () => {
  const response = await axios.get('/projects');
  return response.data;
};

/**
 * Fetches restaurants formatted for the restaurant list table.
 * Duplicate restaurant IDs are collapsed to protect the table from duplicated API rows.
 * @returns {Promise<Array>}
 */
export const getRestaurantListRows = async () => {
  const [restaurants, projects] = await Promise.all([getRestaurants(), getProjects()]);

  return [
    ...new Map(restaurants.map((restaurant) => [restaurant.id, mapRestaurantRow(restaurant, projects)])).values(),
  ];
};

/**
 * Fetches all countries formatted for restaurant filters and forms.
 * @returns {Promise<Array>}
 */
export const getCountries = async () => {
  const response = await axios.get('/countries');
  return response.data.map(mapCountry);
};

/**
 * Fetches all cash systems.
 * @returns {Promise<Array>}
 */
export const getCashSystems = async () => {
  const response = await axios.get('/cash_systems');
  return response.data;
};

/**
 * Fetches all restaurant groups.
 * @returns {Promise<Array>}
 */
export const getRestaurantGroups = async () => {
  const response = await axios.get('/restaurant_groups');
  return response.data;
};

/**
 * Fetches all users.
 * @returns {Promise<Array>}
 */
export const getUsers = async () => {
  const response = await axios.get('/users');
  return response.data;
};

/**
 * Fetches all kiosk types.
 * @returns {Promise<Array>}
 */
export const getKioskTypes = async () => {
  const response = await axios.get('/kiosk-types');
  return response.data;
};

/**
 * Creates a restaurant.
 * @param {Object} restaurantData - Restaurant payload in API format.
 * @returns {Promise<Object|number>}
 */
export const createRestaurant = async (restaurantData) => {
  const response = await axios.post('/restaurants', restaurantData, {
    headers: {
      accept: 'application/json',
    },
  });
  return response.data;
};

/**
 * Updates a restaurant.
 * @param {Object} params
 * @param {number|string} params.restaurantId - The restaurant ID.
 * @param {Object} params.restaurantData - Restaurant payload in API format.
 * @returns {Promise<Object>}
 */
export const updateRestaurant = async ({ restaurantId, restaurantData }) => {
  const response = await axios.put(`/restaurants/${restaurantId}`, restaurantData, {
    headers: {
      accept: 'application/json',
    },
  });
  return response.data;
};

/**
 * Deletes a restaurant.
 * @param {number|string} restaurantId - The restaurant ID.
 * @returns {Promise<Object>}
 */
export const deleteRestaurant = async (restaurantId) => {
  const response = await axios.delete(`/restaurants/${restaurantId}`, {
    headers: {
      Accept: 'application/json, text/plain, */*',
      'Content-Type': 'application/json',
    },
  });
  return response.data;
};
